import { icon } from '@fortawesome/fontawesome-svg-core';
import {
  faPenToSquare,
  faBell,
  faLightbulb
} from '@fortawesome/free-regular-svg-icons';

const warningCautionSvg = '<svg class="markdown-callout-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>';

function renderIconSvg(iconDef) {
  if (!iconDef) {
    return '';
  }

  const rendered = icon(iconDef, { classes: ['markdown-callout-icon-svg'] });
  return rendered?.html?.join('') || '';
}

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizeUrl(rawUrl) {
  if (typeof rawUrl !== 'string') {
    return '#';
  }

  const trimmed = rawUrl.trim();

  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)) {
    return trimmed;
  }

  return '#';
}

const channelMentionIcons = {
  text: '<svg class="discord-mention-icon-svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="3" y="9" width="18" height="2.4" rx="1"></rect><rect x="3" y="14.6" width="18" height="2.4" rx="1"></rect><rect x="7.6" y="3" width="2.4" height="18" rx="1"></rect><rect x="14" y="3" width="2.4" height="18" rx="1"></rect></svg>',
  voice: '<svg class="discord-mention-icon-svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.5 5.3a1 1 0 0 1 1.64-.77l4.2 3.47H21a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-3.66l-4.2 3.47A1 1 0 0 1 11.5 18.7V5.3Z"></path><path d="M8.4 9.4a1.2 1.2 0 0 1 1.6.56 4.8 4.8 0 0 1 0 4.08 1.2 1.2 0 1 1-2.17-1.02 2.4 2.4 0 0 0 0-2.04 1.2 1.2 0 0 1 .57-1.62Z"></path><path d="M4.95 7.68a1.2 1.2 0 0 1 1.61.58 9.6 9.6 0 0 1 0 7.48A1.2 1.2 0 1 1 4.4 14.73a7.2 7.2 0 0 0 0-5.46 1.2 1.2 0 0 1 .55-1.6Z"></path></svg>',
  forum: '<svg class="discord-mention-icon-svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 5.8A2.8 2.8 0 0 1 5.8 3h9.7a2.8 2.8 0 0 1 2.8 2.8v5.9a2.8 2.8 0 0 1-2.8 2.8H9.4l-4.53 3.62A1 1 0 0 1 3 17.34V5.8Z"></path><path d="M9 16.8h6.2a3.8 3.8 0 0 0 3.8-3.8V8.6h.2A2.8 2.8 0 0 1 22 11.4v7.8a1 1 0 0 1-1.62.78l-3.86-3.1H12A3 3 0 0 1 9 16.8Z"></path></svg>',
  post: '<svg class="discord-mention-icon-svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5v8.7a2.5 2.5 0 0 1-2.5 2.5h-8l-5.88 4.7A1 1 0 0 1 3 21.62V6.5Z"></path></svg>',
  thread: '<span class="discord-mention-icon-thread" aria-hidden="true"></span>'
};

function normalizeMentionName(name) {
  if (typeof name !== 'string') {
    return '';
  }

  return name.trim();
}

function renderUserMention(name) {
  const normalized = normalizeMentionName(name);

  if (!normalized) {
    return '';
  }

  return `<span class="discord-mention discord-mention-user" aria-label="User mention">@${normalized}</span>`;
}

function renderChannelMention(name, type = 'text', href = '') {
  const normalizedName = normalizeMentionName(name).replace(/^#+\s*/, '');
  const normalizedType = typeof type === 'string' ? type.toLowerCase() : 'text';
  const mentionType = channelMentionIcons[normalizedType] ? normalizedType : 'text';
  const safeUrl = sanitizeUrl(href);

  if (!normalizedName) {
    return '';
  }

  const pillMarkup = `<span class="discord-mention discord-mention-channel discord-mention-channel-${mentionType}" aria-label="${mentionType} channel mention">` +
    `<span class="discord-mention-icon" aria-hidden="true">${channelMentionIcons[mentionType]}</span>` +
    `${normalizedName}` +
  '</span>';

  if (safeUrl === '#') {
    return pillMarkup;
  }

  return `<a class="discord-mention-channel-link" href="${safeUrl}" target="_blank" rel="noopener noreferrer">${pillMarkup}</a>`;
}

function parseChannelMentionPayload(rawPayload) {
  const payload = normalizeMentionName(rawPayload);

  if (!payload) {
    return {
      type: 'text',
      name: '',
      href: ''
    };
  }

  let basePayload = payload;
  let href = '';
  const linkDelimiters = [':https://', ':http://', ':mailto:'];
  let delimiterIndex = -1;

  for (const delimiter of linkDelimiters) {
    const index = payload.indexOf(delimiter);
    if (index !== -1 && (delimiterIndex === -1 || index < delimiterIndex)) {
      delimiterIndex = index;
    }
  }

  if (delimiterIndex !== -1) {
    basePayload = payload.slice(0, delimiterIndex);
    href = payload.slice(delimiterIndex + 1);
  }

  const typedMatch = basePayload.match(/^(text|voice|forum|post|thread):(.+)$/i);

  if (typedMatch) {
    return {
      type: typedMatch[1].toLowerCase(),
      name: typedMatch[2],
      href
    };
  }

  return {
    type: 'text',
    name: basePayload,
    href
  };
}

function renderDiscordMentions(value) {
  if (typeof value !== 'string' || !value) {
    return value;
  }

  // Supported text mention formats:
  // - User: <@username>
  // - Channel (default text type): <#channel-name>
  // - Channel with explicit type: <#type:channel-name>
  // - Linked channel: <#channel-name:https://example.com>
  // - Linked typed channel: <#type:channel-name:https://example.com>
  return value
    .replace(/&lt;#(.+?)&gt;/g, (_, payload) => {
      const parsed = parseChannelMentionPayload(payload);
      return renderChannelMention(parsed.name, parsed.type, parsed.href);
    })
    .replace(/&lt;@!?(.+?)&gt;/g, (_, name) => renderUserMention(name));
}

export function renderInlineMarkdown(text) {
  if (typeof text !== 'string' || !text.trim()) {
    return '';
  }

  let rendered = escapeHtml(text);

  // Inline code first so formatting markers inside code are not re-processed.
  rendered = rendered.replace(/`([^`]+)`/g, '<code>$1</code>');

  rendered = rendered.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
    const safeUrl = sanitizeUrl(url);
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  rendered = renderDiscordMentions(rendered);

  rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  rendered = rendered.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  return rendered;
}

export function renderMarkdown(text) {
  const markdownText = Array.isArray(text)
    ? text
        .filter((line) => typeof line === 'string')
        .join('\n')
    : text;

  if (typeof markdownText !== 'string' || !markdownText.trim()) {
    return '';
  }

  const lines = markdownText.replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let paragraphBuffer = [];
  let inUnorderedList = false;
  let inOrderedList = false;
  let inBlockquote = false;
  let blockquoteBuffer = [];
  let inCallout = false;
  let calloutType = 'note';
  let calloutBuffer = [];

  const calloutConfig = {
    note: {
      title: 'NOTE!',
      iconSvg: renderIconSvg(faPenToSquare)
    },
    important: {
      title: 'IMPORTANT!',
      iconSvg: renderIconSvg(faBell)
    },
    warning: {
      title: 'WARNING!',
      iconSvg: warningCautionSvg
    },
    tip: {
      title: 'TIP!',
      iconSvg: renderIconSvg(faLightbulb)
    }
  };

  const flushBlockquote = () => {
    if (!inBlockquote) {
      return;
    }

    const quoteText = blockquoteBuffer.join(' ').trim();
    html.push(`<blockquote>${renderInlineMarkdown(quoteText)}</blockquote>`);
    inBlockquote = false;
    blockquoteBuffer = [];
  };

  const flushCallout = () => {
    if (!inCallout) {
      return;
    }

    const config = calloutConfig[calloutType] || calloutConfig.note;
    const body = calloutBuffer
      .map((line) => renderInlineMarkdown(line.trim()))
      .filter(Boolean)
      .join('<br>');

    html.push(
      `<div class="markdown-callout markdown-callout-${calloutType}" role="note">` +
        `<p class="markdown-callout-title"><span class="markdown-callout-icon" aria-hidden="true">${config.iconSvg}</span>${config.title}</p>` +
        (body ? `<p>${body}</p>` : '') +
      '</div>'
    );

    inCallout = false;
    calloutType = 'note';
    calloutBuffer = [];
  };

  const closeLists = () => {
    if (inUnorderedList) {
      html.push('</ul>');
      inUnorderedList = false;
    }

    if (inOrderedList) {
      html.push('</ol>');
      inOrderedList = false;
    }
  };

  const flushParagraph = () => {
    if (!paragraphBuffer.length) {
      return;
    }

    const paragraphText = paragraphBuffer.join(' ').trim();
    if (paragraphText) {
      html.push(`<p>${renderInlineMarkdown(paragraphText)}</p>`);
    }

    paragraphBuffer = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (inCallout) {
      const calloutLineMatch = line.match(/^>\s?(.*)$/);

      if (calloutLineMatch) {
        calloutBuffer.push(calloutLineMatch[1]);
        return;
      }

      flushCallout();
    }

    if (inBlockquote) {
      const blockquoteLineMatch = line.match(/^>\s?(.*)$/);

      if (blockquoteLineMatch) {
        blockquoteBuffer.push(blockquoteLineMatch[1]);
        return;
      }

      flushBlockquote();
    }

    if (!line) {
      flushParagraph();
      flushCallout();
      flushBlockquote();
      closeLists();
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushCallout();
      flushBlockquote();
      closeLists();
      const headingLevel = headingMatch[1].length;
      html.push(`<h${headingLevel}>${renderInlineMarkdown(headingMatch[2])}</h${headingLevel}>`);
      return;
    }

    if (/^([-*_])\1{2,}$/.test(line)) {
      flushParagraph();
      flushCallout();
      flushBlockquote();
      closeLists();
      html.push('<hr>');
      return;
    }

    const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      flushCallout();
      flushBlockquote();
      if (inOrderedList) {
        html.push('</ol>');
        inOrderedList = false;
      }
      if (!inUnorderedList) {
        html.push('<ul>');
        inUnorderedList = true;
      }
      html.push(`<li>${renderInlineMarkdown(unorderedMatch[1])}</li>`);
      return;
    }

    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      flushCallout();
      flushBlockquote();
      if (inUnorderedList) {
        html.push('</ul>');
        inUnorderedList = false;
      }
      if (!inOrderedList) {
        html.push('<ol>');
        inOrderedList = true;
      }
      html.push(`<li>${renderInlineMarkdown(orderedMatch[1])}</li>`);
      return;
    }

    const calloutStartMatch = line.match(/^>\s*\[!(NOTE|IMPORTANT|WARNING|TIP)\]\s*(.*)$/i);
    if (calloutStartMatch) {
      flushParagraph();
      flushBlockquote();
      closeLists();

      inCallout = true;
      calloutType = calloutStartMatch[1].toLowerCase();
      calloutBuffer = [];

      if (calloutStartMatch[2]) {
        calloutBuffer.push(calloutStartMatch[2]);
      }

      return;
    }

    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      closeLists();
      inBlockquote = true;
      blockquoteBuffer.push(quoteMatch[1]);
      return;
    }

    flushCallout();
    flushBlockquote();
    closeLists();
    paragraphBuffer.push(line);
  });

  flushParagraph();
  flushCallout();
  flushBlockquote();
  closeLists();

  return html.join('');
}
